import { createSlice } from "@reduxjs/toolkit";
import { apiFetch, getFullImageUrl } from "../api";
import { toast } from "react-hot-toast";
import { getFallbackImage } from "../utils/imageLoader";

// Helper function to safely calculate
const calculateTotals = (items) => {
  let total = 0;
  let itemCount = 0;
  items.forEach(item => {
    const price = Number(item.price) || 0;
    const quantity = Number(item.quantity) || 0;
    if (price > 0 && quantity > 0) {
      total += price * quantity;
      itemCount += quantity;
    }
  });
  return { total, itemCount };
};

// Unique cart key per variant for guest carts
const getCartKey = (item) => {
  if (!item) return '';
  const id = item.id || item.product || '';
  const variant = item.variantId || item.variant || '';
  const color = item.selectedColor || '';
  const size = item.selectedSize || '';
  return `${id}::${variant}::${color}::${size}`;
};

// Extract color/size from variant ID by searching variants array
const extractColorSizeFromVariant = (variantId, productVariants) => {
  if (!variantId || !productVariants || !Array.isArray(productVariants)) {
    return { color: '', size: '' };
  }
  // Find the variant object matching the given variantId
  const variant = productVariants.find(v => v._id === variantId);
  if (!variant) return { color: '', size: '' };
  const color = Array.isArray(variant.color) ? variant.color[0] : variant.color;
  const size = Array.isArray(variant.size) ? variant.size[0] : variant.size;
  return { color: color || '', size: size || '' };
};

const cartSlice = createSlice({
  name: "cart",
  initialState: {
    items: [],
    total: 0,
    itemCount: 0,
  },
  reducers: {
    setCart: (state, action) => {
      // Ensure each server item has a stable key
      state.items = (action.payload || []).map(it => ({
        ...it,
        key: it.key || getCartKey(it)
      }));
      const { total, itemCount } = calculateTotals(state.items);
      state.total = total;
      state.itemCount = itemCount;
    },
    addToCart: (state, action) => {
      // Add item or update quantity if already exists
      const productToAdd = action.payload;
      const quantityToAdd = Number(productToAdd.quantity) || 1;
      const key = productToAdd.key || getCartKey(productToAdd);
      const existingItem = state.items.find(item => (item.key || getCartKey(item)) === key);

      // Update quantity if exists, else add new item
      if (existingItem) {
        existingItem.quantity = (Number(existingItem.quantity) || 0) + quantityToAdd;
      } else {
        state.items.push({
             ...productToAdd,
             key,
             price: Number(productToAdd.price) || 0,
             quantity: quantityToAdd
        });
      }
      // Recalculate totals
      const { total, itemCount } = calculateTotals(state.items);
      state.total = total;
      state.itemCount = itemCount;
    },
    removeFromCart: (state, action) => {
      const payload = action.payload;
      // Backward compatibility: if a string/id is passed, remove all with that id
      if (typeof payload === 'string') {
        state.items = state.items.filter(item => item.id !== payload);
      } else {
        // Prefer precise removal by composite key
        const key = payload?.key || getCartKey(payload);
        state.items = state.items.filter(item => (item.key || getCartKey(item)) !== key);
      }
      const { total, itemCount } = calculateTotals(state.items);
      state.total = total;
      state.itemCount = itemCount;
    },
    updateQuantity: (state, action) => {
      const { id, quantity } = action.payload;
      const key = action.payload?.key || getCartKey(action.payload || {});
      let item = state.items.find(it => (it.key || getCartKey(it)) === key);
      if (!item) {
        // Fallback to first match by id if key not found
        item = state.items.find(it => it.id === id);
      }
      const newQuantity = Number(quantity) || 0;

      if (item) {
        item.quantity = newQuantity;
        if (item.quantity <= 0) {
            const removeKey = item.key || getCartKey(item);
            state.items = state.items.filter(i => (i.key || getCartKey(i)) !== removeKey);
        }
      }
      const { total, itemCount } = calculateTotals(state.items);
      state.total = total;
      state.itemCount = itemCount;
    },
    clearCart: (state) => {
      state.items = [];
      state.total = 0;
      state.itemCount = 0;
    }
  }
});

export const { setCart, addToCart, removeFromCart, updateQuantity, clearCart } = cartSlice.actions;
export default cartSlice.reducer;

// ----------------------------------------------------
// THUNKS FOR ASYNCHRONOUS API CALLS
// ----------------------------------------------------

export const fetchUserCart = () => async (dispatch) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    const data = await apiFetch('/api/cart');

    if (!data || !data.cart || !Array.isArray(data.cart.items)) {
      dispatch(setCart([]));
      return;
    }

    const validItems = data.cart.items.filter(item => item.product !== null);

    const transformedData = validItems.map(item => {
        let firstImage = getFallbackImage();
        let allImages = [];

        if (item.product.images && Array.isArray(item.product.images) && item.product.images.length > 0) {
            const firstImageData = item.product.images[0];
            firstImage = getFullImageUrl(typeof firstImageData === 'string' ? firstImageData : firstImageData?.url);
            allImages = item.product.images.map(imgData =>
                getFullImageUrl(typeof imgData === 'string' ? imgData : imgData?.url)
            );
        }

        let currentPrice = item.price;
        let selectedColor = item.selectedColor || '';
        let selectedSize = item.selectedSize || '';
        
        // If backend sends variants, extract from them
        if (item.product.variants && Array.isArray(item.product.variants) && item.variant) {
            const matchingVariant = item.product.variants.find(v => v._id === item.variant);
            if (matchingVariant) {
                if (matchingVariant.price) {
                    currentPrice = Number(matchingVariant.price);
                }
                if (!selectedColor && matchingVariant.color) {
                    selectedColor = Array.isArray(matchingVariant.color) ? matchingVariant.color[0] : matchingVariant.color;
                }
                if (!selectedSize && matchingVariant.size) {
                    selectedSize = Array.isArray(matchingVariant.size) ? matchingVariant.size[0] : matchingVariant.size;
                }
            }
        }
        
        // Fallback: if variants still missing, use helper to extract from variants array
        if (!selectedColor || !selectedSize) {
            const extracted = extractColorSizeFromVariant(item.variant, item.product.variants || []);
            selectedColor = selectedColor || extracted.color;
            selectedSize = selectedSize || extracted.size;
        }

        // Construct transformed cart item
        const transformed = {
          ...item.product,
          image: firstImage,
          images: allImages,
          price: currentPrice,
          quantity: item.quantity,
          id: item.product._id,
          cartItemId: item._id,
          title: item.product.name,
          description: item.product.description, 
          selectedColor: selectedColor,
          selectedSize: selectedSize,
          variantId: item.variant
        };
        return { ...transformed, key: getCartKey(transformed) };
    });

    dispatch(setCart(transformedData));
  } catch (error) {
    console.error("Error fetching cart:", error);
    dispatch(setCart([]));
  }
};

// Add product to cart with variant handling
export const addProductToCart = (product, quantity = 1, options = { showNotification: true }) => async (dispatch, getState) => {
  try {
    if (!localStorage.getItem('token')) {
      dispatch(addToCart({ ...product, quantity }));
      if (options.showNotification) {
        window.dispatchEvent(new CustomEvent('cartNotification', { detail: { type: 'cart', subType: 'add', message: 'Added to cart', item: product }}));
      }
      return;
    }
    // For logged-in users, check if item exists in cart
    const currentItems = getState().cart.items;
    const existingItem = currentItems.find(item => item.id === product.id);
    if (existingItem) {
      await apiFetch('/api/cart/update', {
        method: 'PUT',
        body: { itemId: existingItem.cartItemId, quantity: existingItem.quantity + quantity }
      });
      await dispatch(fetchUserCart());
      if (options.showNotification) {
        window.dispatchEvent(new CustomEvent('cartNotification', { detail: { type: 'cart', subType: 'add', message: 'Updated quantity', item: product }}));
      }
    } else {
      await apiFetch('/api/cart/add', {
        method: 'POST',
        body: { product: product.id, quantity, variant: product.variantId, selectedColor: product.selectedColor, selectedSize: product.selectedSize }
      });
      await dispatch(fetchUserCart());
      if (options.showNotification) {
         window.dispatchEvent(new CustomEvent('cartNotification', { detail: { type: 'cart', subType: 'add', message: 'Added to cart', item: product }}));
      }
    }
    // No need to dispatch addToCart locally since we refresh from server
  } catch (error) {
    if (options.showNotification) {
      toast.error(error.message || "Failed to add to cart.");
    } else {
      console.error("Silent cart add failed:", error);
    }
  }
};
// Remove product from cart with variant handling
export const removeProductFromCart = (product) => async (dispatch) => {
    try {
        if (!localStorage.getItem('token')) {
            // Remove precise variant for guests
            dispatch(removeFromCart(product));
            window.dispatchEvent(new CustomEvent('cartNotification', { detail: { type: 'cart', subType: 'remove', message: 'Removed from cart', item: product }}));
            return;
        }
        const cartItemId = product.cartItemId;
        if (!cartItemId) {
            console.error("Missing cartItemId for removal.");
            toast.error("Error removing item. Please try again.");
            return;
        }
        await apiFetch(`/api/cart/remove/${cartItemId}`, { method: 'DELETE' });
        // Remove only the specific variant locally
        dispatch(removeFromCart(product));
        window.dispatchEvent(new CustomEvent('cartNotification', { detail: { type: 'cart', subType: 'remove', message: 'Removed from cart', item: product }}));
    } catch (error) {
        toast.error(error.message || "Failed to remove from cart.");
    }
};

// Update product quantity in cart with variant handling
export const updateCartItemQuantity = (product, quantity, options = { showNotification: true }) => async (dispatch) => {
    try {
        if (!localStorage.getItem('token')) {
            // Update precise variant for guests
            dispatch(updateQuantity({ id: product.id, variantId: product.variantId, selectedColor: product.selectedColor, selectedSize: product.selectedSize, quantity }));
            return;
        }
        if (!product.cartItemId) {
            console.error("Missing cartItemId for update.");
            if (options.showNotification) toast.error("Error updating item. Please try again.");
            return;
        }
        await apiFetch('/api/cart/update', {
          method: 'PUT',
          body: { itemId: product.cartItemId, quantity }
        });
        // Refresh from server to ensure the correct variant is updated
        await dispatch(fetchUserCart());
    } catch (error) {
        if (options.showNotification) {
            toast.error(error.message || "Failed to update cart item.");
        } else {
            console.error("Silent cart update failed:", error);
        }
    }
};

// Clear entire user cart
export const clearUserCart = () => async (dispatch, getState) => {
    try {
        if (!localStorage.getItem('token')) {
            dispatch(clearCart());
            return;
        }
        const currentItems = getState().cart.items;
        for (const item of currentItems) {
            if (item.cartItemId) {
                await apiFetch(`/api/cart/remove/${item.cartItemId}`, { method: 'DELETE' });
            }
        }
        dispatch(clearCart());
        window.dispatchEvent(new CustomEvent('cartNotification', { detail: { type: 'cart', subType: 'remove', message: 'Cart cleared', item: null }}));
    } catch (error) {
        toast.error(error.message || "Failed to clear cart.");
    }
};